import { useState } from "react";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { theme } from "../../../theme";
import { useSendInvoiceMessageToClient } from "../../../graphql/hooks/invoices/useSendInvoiceMessageToClient";
import { useClientMessageEmailTemplates } from "../../../graphql/hooks/contentful/useClientMessageEmaiTemplates";
import { validateEmail } from "../../../utils/validation";
import Modal from "../Modal";
import TextField from "../../atoms/inputs/TextField";
import MultilineTextField from "../../atoms/inputs/MultilineTextField";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Alert from "../Alert";
import { useTranslation } from "react-i18next";
import { ChatIcon } from "../../atoms/icons/ChatIcon";
import Checkbox from "../../atoms/inputs/Checkbox";
import Select from "../../atoms/inputs/Select";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import { Col, Row } from "react-grid-system";

interface Props {
  invoiceIds: string[];
  clientIdentifier: string;
  buttonType?: "outlined" | "outlinedSmallDiscrete";
  buttonTitle?: string;
  renderTrigger?: (onClick: () => void) => React.ReactNode;
}

const SendInvoiceMessageToClientButton: React.FC<Props> = ({
  buttonType,
  buttonTitle,
  invoiceIds,
  clientIdentifier,
  renderTrigger,
}) => {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [includeInvoicesAsAttachments, setIncludeInvoicesAsAttachments] =
    useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const {
    clientMessageTemplates,
    error: clientMessageTemplatesError,
    loading: clientMessageTemplatesLoading,
  } = useClientMessageEmailTemplates({ skip: !modalOpen });

  const getClientMessageEmailOptions = (): Array<{
    value: string;
    label: string;
  }> => {
    if (!clientMessageTemplates || !clientMessageTemplates.items) return [];

    const templateOptions = clientMessageTemplates.items.map((item) => ({
      value: item.id,
      label: item.title,
    }));

    return [{ value: "", label: "Välj mall" }, ...templateOptions];
  };

  const onSelectClientMessageTemplate = (clientMessageTemplateId: string) => {
    setSelectedTemplateId(clientMessageTemplateId);
    setMessage(
      clientMessageTemplates?.items?.find(
        (item) => item.id === clientMessageTemplateId,
      )?.content || "",
    );
  };

  const onCompletedSendMessage = (res?: {
    invoice?: { sendInvoiceMessageToClient?: { success?: boolean } };
  }) => {
    if (res?.invoice?.sendInvoiceMessageToClient?.success === true) {
      setSuccess(true);
      setErrorMessage("");
      setSubject("");
      setRecipientEmail("");
      setMessage("");
    } else {
      setSuccess(false);
      setErrorMessage(t("errors.couldNotSendMessageToClient"));
    }
  };
  const onErrorSendMessage = (e?: any) => {
    setSuccess(false);
    setErrorMessage(e?.toString() || t("errors.couldNotSendMessageToClient"));
  };
  const { sendInvoiceMessageToClient, error, loading } =
    useSendInvoiceMessageToClient({
      onCompleted: onCompletedSendMessage,
      onError: onErrorSendMessage,
    });

  const onClickSendMessage = async () => {
    if (!validateEmail(recipientEmail)) {
      setErrorMessage(t("documentHandler.invalidClientEmailOrMessage"));
      return;
    }

    const input: {
      clientIdentifier: string;
      invoiceIds?: string[];
      subject: string;
      recipientEmail: string;
      message: string;
    } = {
      clientIdentifier: clientIdentifier,
      invoiceIds: includeInvoicesAsAttachments ? invoiceIds : undefined,
      subject: subject,
      recipientEmail: recipientEmail,
      message: message,
    };

    try {
      await sendInvoiceMessageToClient({ variables: { input } });
    } catch (e) {
      onErrorSendMessage(e);
    }
  };

  return (
    <div>
      {renderTrigger ? (
        renderTrigger(() => setModalOpen(true))
      ) : buttonType === "outlined" ? (
        <OutlinedButton
          color={theme.colors.primary}
          title={buttonTitle || t("documentHandler.sendAnEmailToTheClient")}
          onClick={() => setModalOpen(true)}
          medium
          icon={<ChatIcon color={theme.colors.primary} />}
        />
      ) : buttonType === "outlinedSmallDiscrete" ? (
        <OutlinedButton
          color={theme.colors.text}
          title={buttonTitle || t("documentHandler.sendAnEmailToTheClient")}
          onClick={() => setModalOpen(true)}
          icon={<ChatIcon color={theme.colors.text} />}
        />
      ) : null}

      {modalOpen && (
        <Modal
          closeIcon
          onClose={() => setModalOpen(false)}
          title={t("documentHandler.sendAnEmailToTheClient")}
          width={600}
          actionButtons={{
            defaultButton: {
              buttonTitle: t("common.cancel"),
              onClick: () => setModalOpen(false),
            },
            actionButton: {
              buttonTitle: t("random.send"),
              onClick: onClickSendMessage,
              loading: loading,
            },
          }}
        >
          <div>
            <Row>
              <Col lg={6}>
                <TextField
                  label={t("random.recipientEmail")}
                  value={recipientEmail}
                  onChange={setRecipientEmail}
                  fullWidth
                />
              </Col>
              <Col lg={6}>
                <TextField
                  label={t("random.subject")}
                  value={subject}
                  onChange={setSubject}
                  fullWidth
                />
              </Col>
            </Row>
            <HorizontalSpacer spacing={1} />
            {clientMessageTemplatesLoading ? (
              <SmallLoadingSpinner />
            ) : (
              <Select
                options={getClientMessageEmailOptions()}
                value={selectedTemplateId}
                onChange={onSelectClientMessageTemplate}
                label={t("invoices.selectClientMessageTemplate")}
              />
            )}
            <HorizontalSpacer spacing={1} />
            <MultilineTextField
              label={t("random.message")}
              value={message}
              onChange={setMessage}
            />
            <HorizontalSpacer spacing={1} />
            <Checkbox
              title="Inkludera fil/filer som bilaga"
              checked={includeInvoicesAsAttachments}
              onChange={setIncludeInvoicesAsAttachments}
              disabled={invoiceIds.length === 0}
            />
            <HorizontalSpacer spacing={1} />
            {clientMessageTemplatesError && (
              <Alert
                type="error"
                title={t("errors.couldNotLoadClientMessageTemplates")}
                message={clientMessageTemplatesError.message}
                small
              />
            )}
            <HorizontalSpacer spacing={1} />
            {(errorMessage || error) && (
              <Alert
                type="error"
                title={t("errors.couldNotSendMessageToClient")}
                message={errorMessage || error?.message}
              />
            )}
            {success && (
              <Alert type="success" title={t("random.messageSent")} />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SendInvoiceMessageToClientButton;
